// Lightweight markdown renderer with NO external dependency.
// Supports headings, bold/italic/code, lists, code blocks, blockquotes, horizontal
// rules, and pipe-delimited tables. Designed to render executive-clean output on
// the Acquigine dark theme — raw markdown characters never reach the user.
export default function Markdown({ children }) {
  if (!children) return null;
  const text = String(children);

  // Split into logical blocks. Each block is one paragraph, table, list, etc.
  const blocks = splitBlocks(text);

  return (
    <div className="space-y-3" data-testid="markdown-rendered">
      {blocks.map((b, i) => renderBlock(b, i))}
    </div>
  );
}

function splitBlocks(text) {
  // Normalize line endings, then split on blank lines.
  return text.replace(/\r\n/g, "\n").split(/\n{2,}/).map((b) => b.trimEnd()).filter(Boolean);
}

function renderBlock(block, key) {
  // Code fence
  const codeMatch = block.match(/^```(?:\w+)?\n([\s\S]*?)\n?```$/);
  if (codeMatch) {
    return (
      <pre key={key} className="bg-black/40 border border-white/10 rounded-sm p-3 text-xs font-mono overflow-x-auto">
        <code>{codeMatch[1]}</code>
      </pre>
    );
  }
  // Horizontal rule (--- or *** or ___ on its own line)
  if (/^(-{3,}|\*{3,}|_{3,})$/.test(block.trim())) {
    return <hr key={key} className="border-0 border-t border-emerald-500/30 my-2" />;
  }
  // Heading
  const h = block.match(/^(#{1,6})\s+(.+)$/);
  if (h) {
    const level = h[1].length;
    const sizeCls = ["text-2xl", "text-xl", "text-lg", "text-base", "text-sm", "text-sm"][level - 1];
    return (
      <h3 key={key} className={`${sizeCls} font-heading font-semibold tracking-tight text-emerald-300 mt-2`}>
        <span className="inline-block border-b-2 border-emerald-500/40 pb-1 uppercase tracking-widest text-[11px]">
          {renderInline(h[2])}
        </span>
      </h3>
    );
  }
  // Table (must contain a header row + separator row)
  if (isTable(block)) {
    return renderTable(block, key);
  }
  // Blockquote (one or more lines starting with >)
  if (/^>\s?/.test(block.split("\n")[0])) {
    const lines = block.split("\n").map((l) => l.replace(/^>\s?/, ""));
    return (
      <blockquote
        key={key}
        className="border-l-2 border-emerald-500/60 bg-emerald-500/[0.06] pl-3 py-2 text-slate-300 italic text-sm"
      >
        {lines.map((l, j) => <div key={j}>{renderInline(l)}</div>)}
      </blockquote>
    );
  }
  // Unordered list
  if (/^[\-\*]\s+/.test(block.split("\n")[0])) {
    return (
      <ul key={key} className="list-disc pl-5 space-y-1">
        {block.split("\n").map((line, j) => {
          const m = line.match(/^[\-\*]\s+(.+)$/);
          return m ? <li key={j}>{renderInline(m[1])}</li> : null;
        })}
      </ul>
    );
  }
  // Ordered list
  if (/^\d+\.\s+/.test(block.split("\n")[0])) {
    return (
      <ol key={key} className="list-decimal pl-5 space-y-1">
        {block.split("\n").map((line, j) => {
          const m = line.match(/^\d+\.\s+(.+)$/);
          return m ? <li key={j}>{renderInline(m[1])}</li> : null;
        })}
      </ol>
    );
  }
  // Default paragraph
  return <p key={key} className="text-sm leading-relaxed text-slate-200 whitespace-pre-wrap">{renderInline(block)}</p>;
}

function isTable(block) {
  const lines = block.split("\n");
  if (lines.length < 2) return false;
  const sep = lines[1]?.trim();
  return /^\|/.test(lines[0]) && /^\|?\s*:?-{3,}/.test(sep || "");
}

function parseRow(line) {
  return line.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
}

function renderTable(block, key) {
  const [headerLine, , ...bodyLines] = block.split("\n");
  const headers = parseRow(headerLine);
  const rows = bodyLines.filter((l) => l.trim()).map(parseRow);
  return (
    <div key={key} className="overflow-x-auto" data-testid="md-table">
      <table className="w-full text-sm border border-white/10 rounded-sm overflow-hidden">
        <thead>
          <tr className="bg-slate-900/70 border-b border-emerald-500/40">
            {headers.map((h, j) => (
              <th key={j} className="px-3 py-2 text-left text-[11px] uppercase tracking-widest text-emerald-300 font-semibold">
                {renderInline(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? "bg-white/[0.01]" : "bg-white/[0.04]"}>
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2 text-slate-200 border-t border-white/5">
                  {renderInline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderInline(text) {
  // Order matters: bold first (so **bold** with *italic* inside doesn't confuse italic regex)
  let parts = [text];
  parts = splitMap(parts, /\*\*(.+?)\*\*/g, (m, key) => (
    <strong key={key} className="font-semibold text-slate-50">{m}</strong>
  ));
  parts = splitMap(parts, /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, (m, key) => <em key={key}>{m}</em>);
  parts = splitMap(parts, /`([^`]+)`/g, (m, key) => (
    <code key={key} className="bg-black/40 px-1 py-0.5 rounded text-[11px] font-mono text-emerald-300">{m}</code>
  ));
  // Auto-bold dollar amounts and large numbers
  parts = splitMap(parts, /(\$[\d,]+(?:\.\d+)?[kKmMbB]?)/g, (m, key) => (
    <strong key={key} className="font-mono tabular-nums text-emerald-300">{m}</strong>
  ));
  return parts;
}

function splitMap(parts, regex, render) {
  const out = [];
  let k = 0;
  for (const p of parts) {
    if (typeof p !== "string") { out.push(p); continue; }
    let lastIdx = 0;
    let m;
    const re = new RegExp(regex.source, regex.flags);
    while ((m = re.exec(p)) !== null) {
      if (m.index > lastIdx) out.push(p.slice(lastIdx, m.index));
      out.push(render(m[1], `k-${k++}`));
      lastIdx = m.index + m[0].length;
    }
    if (lastIdx < p.length) out.push(p.slice(lastIdx));
  }
  return out;
}
